import React, { useState, useEffect } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './ResumenFluidez.css';
// Opciones para mostrar cantidad + porcentaje en los Doughnut
const doughnutOptions = {
  responsive: true,
  plugins: {
    legend: { position: 'top' },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.parsed;
          const data = context.dataset.data;
          const total = data.reduce((sum, v) => sum + v, 0);
          const percent = total > 0
            ? ((value / total) * 100).toFixed(1)
            : '0.0';
          return `${label}: ${value} (${percent}%)`;
        }
      }
    }
  }
};
// —————————————————————————————————
// 1) Registro de componentes Chart.js
// —————————————————————————————————
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// —————————————————————————————————
// 2) Mapeo de grupos y orden
// —————————————————————————————————
const groupMapping = {
  PK: ['PKA','PKB'], K: ['KA','KB'],
  1: ['1A','1B'], 2: ['2A','2B'], 3: ['3A','3B'],
  4: ['4A','4B'], 5: ['5A','5B'], 6: ['6A','6B'],
  7: ['7A','7B'], 8: ['8A','8B'],
  I: ['IA','IB'], II: ['IIA','IIB'],
  III: ['IIIA','IIIB'], IV: ['IVA','IVB']
};
const groupOrder = ['PK','K','1','2','3','4','5','6','7','8','I','II','III','IV'];

// —————————————————————————————————
// 3) Umbrales Fluidez Lectora
// —————————————————————————————————
const readingThresholds = {
  1: { muyRapida:{min:56,max:Infinity}, rapida:{min:47,max:55},
       mediaAlta:{min:38,max:46}, mediaBaja:{min:29,max:37},
       lenta:{min:22,max:28}, muyLenta:{min:21,max:21},
       fueraTabla:{min:1,max:20}, noLector:{min:0,max:0} },
  2: { muyRapida:{min:84,max:Infinity}, rapida:{min:74,max:83},
       mediaAlta:{min:64,max:73}, mediaBaja:{min:54,max:63},
       lenta:{min:45,max:53}, muyLenta:{min:42,max:42},
       fueraTabla:{min:21,max:41}, noLector:{min:0,max:20} },
  3: { muyRapida:{min:112,max:Infinity}, rapida:{min:100,max:111},
       mediaAlta:{min:88,max:99}, mediaBaja:{min:76,max:87},
       lenta:{min:64,max:75}, muyLenta:{min:63,max:63},
       fueraTabla:{min:21,max:62}, noLector:{min:0,max:20} },
  4: { muyRapida:{min:140,max:Infinity}, rapida:{min:125,max:139},
       mediaAlta:{min:111,max:124}, mediaBaja:{min:97,max:110},
       lenta:{min:85,max:96}, muyLenta:{min:84,max:84},
       fueraTabla:{min:21,max:83}, noLector:{min:0,max:20} },
  5: { muyRapida:{min:168,max:Infinity}, rapida:{min:150,max:167},
       mediaAlta:{min:136,max:149}, mediaBaja:{min:120,max:135},
       lenta:{min:104,max:119}, muyLenta:{min:103,max:103},
       fueraTabla:{min:21,max:102}, noLector:{min:0,max:20} },
  6: { muyRapida:{min:196,max:Infinity}, rapida:{min:178,max:195},
       mediaAlta:{min:161,max:177}, mediaBaja:{min:143,max:160},
       lenta:{min:125,max:142}, muyLenta:{min:124,max:124},
       fueraTabla:{min:21,max:123}, noLector:{min:0,max:20} },
  7: { muyRapida:{min:196,max:Infinity}, rapida:{min:178,max:195},
       mediaAlta:{min:161,max:177}, mediaBaja:{min:143,max:160},
       lenta:{min:125,max:142}, muyLenta:{min:124,max:124},
       fueraTabla:{min:21,max:123}, noLector:{min:0,max:20} },
  8: { muyRapida:{min:196,max:Infinity}, rapida:{min:178,max:195},
       mediaAlta:{min:161,max:177}, mediaBaja:{min:143,max:160},
       lenta:{min:125,max:142}, muyLenta:{min:124,max:124},
       fueraTabla:{min:21,max:123}, noLector:{min:0,max:20} },
};
['PK','K'].forEach(g => readingThresholds[g] = null);

// Para I, II, III y IV, reutilizamos los umbrales de 8°:
['I','II','III','IV'].forEach(g => {
  readingThresholds[g] = { ...readingThresholds['8'] };
});

const readingLabels = [
  'Muy rápida','Rápida','Media alta','Media baja',
  'Lenta','Muy lenta','Fuera de tabla','No lector'
];
const readingKeys = [
  'muyRapida','rapida','mediaAlta','mediaBaja',
  'lenta','muyLenta','fueraTabla','noLector'
];
const readingColors = [
  '#2ecc71','#27ae60','#16a085','#f1c40f',
  '#f1c40f','#f39c12','#e74c3c','#c0392b'
];

function classifyReading(valor, group) {
  const spec = readingThresholds[group];
  if (!spec) return null;
  for (let key of readingKeys) {
    const { min, max } = spec[key];
    if (valor >= min && valor <= max) return key;
  }
  return null;
}

// —————————————————————————————————
// 4) Umbrales Matemáticas (7 categorías)
// —————————————————————————————————
const mathAddThresholds = {
  '1': { fueraTabla:{min:0,max:7}, muyLento:{min:8,max:13}, lento:{min:14,max:20},
         medio:{min:21,max:27}, rapido:{min:28,max:35}, veloz:{min:36,max:42},
         automatizado:{min:43,max:Infinity} },
  '2': { fueraTabla:{min:0,max:9}, muyLento:{min:10,max:15}, lento:{min:16,max:23},
         medio:{min:24,max:31}, rapido:{min:32,max:40}, veloz:{min:41,max:47},
         automatizado:{min:48,max:Infinity} },
  '3': { fueraTabla:{min:0,max:13}, muyLento:{min:14,max:20}, lento:{min:21,max:29},
         medio:{min:30,max:41}, rapido:{min:42,max:48}, veloz:{min:49,max:56},
         automatizado:{min:57,max:Infinity} },
  '4': { fueraTabla:{min:0,max:17}, muyLento:{min:18,max:24}, lento:{min:25,max:32},
         medio:{min:33,max:41}, rapido:{min:42,max:49}, veloz:{min:50,max:57},
         automatizado:{min:58,max:Infinity} },
  '5': { fueraTabla:{min:0,max:19}, muyLento:{min:20,max:26}, lento:{min:27,max:34},
         medio:{min:35,max:43}, rapido:{min:44,max:51}, veloz:{min:52,max:58},
         automatizado:{min:59,max:Infinity} },
  '6': { fueraTabla:{min:0,max:21}, muyLento:{min:22,max:28}, lento:{min:29,max:36},
         medio:{min:37,max:45}, rapido:{min:46,max:53}, veloz:{min:54,max:58},
         automatizado:{min:59,max:Infinity} },
  '7': { fueraTabla:{min:0,max:22}, muyLento:{min:23,max:29}, lento:{min:30,max:37},
         medio:{min:38,max:46}, rapido:{min:47,max:54}, veloz:{min:55,max:58},
         automatizado:{min:59,max:Infinity} },
  '8': { fueraTabla:{min:0,max:23}, muyLento:{min:24,max:30}, lento:{min:31,max:38},
         medio:{min:39,max:47}, rapido:{min:48,max:55}, veloz:{min:56,max:58},
         automatizado:{min:59,max:Infinity} },
};

const mathMultThresholds = {
  '4': { fueraTabla:{min:0,max:19}, muyLento:{min:20,max:25}, lento:{min:26,max:30},
         medio:{min:31,max:35}, rapido:{min:36,max:40}, veloz:{min:41,max:45},
         automatizado:{min:46,max:Infinity} },
  '5': { fueraTabla:{min:0,max:24}, muyLento:{min:25,max:30}, lento:{min:31,max:36},
         medio:{min:37,max:42}, rapido:{min:43,max:48}, veloz:{min:49,max:55},
         automatizado:{min:56,max:Infinity} },
  '6': { fueraTabla:{min:0,max:29}, muyLento:{min:30,max:35}, lento:{min:36,max:40},
         medio:{min:41,max:45}, rapido:{min:46,max:50}, veloz:{min:51,max:57},
         automatizado:{min:58,max:Infinity} },
  '7': { fueraTabla:{min:0,max:30}, muyLento:{min:31,max:36}, lento:{min:37,max:41},
         medio:{min:42,max:46}, rapido:{min:47,max:51}, veloz:{min:52,max:57},
         automatizado:{min:58,max:Infinity} },
  '8': { fueraTabla:{min:0,max:31}, muyLento:{min:32,max:37}, lento:{min:38,max:42},
         medio:{min:43,max:47}, rapido:{min:48,max:52}, veloz:{min:53,max:58},
         automatizado:{min:59,max:Infinity} },
};

// --- INICIO DE LA CORRECCIÓN ---
// Reutilizar los umbrales de 8° para los grupos I, II, III, y IV en matemáticas
['I', 'II', 'III', 'IV'].forEach(g => {
  mathAddThresholds[g] = { ...mathAddThresholds['8'] };
  mathMultThresholds[g] = { ...mathMultThresholds['8'] };
});
// --- FIN DE LA CORRECCIÓN ---


const mathKeys   = ['fueraTabla','muyLento','lento','medio','rapido','veloz','automatizado'];
const mathLabels = ['Fuera de rango','Muy lento','Lento','Medio','Rápido','Veloz','Automatizado'];
const mathColors = [
  '#e74c3c', // rojo
  '#f39c12', // naranja (muy lento)
  '#f1c40f', // amarillo (lento)
  '#f7dc6f', // amarillo claro (medio)
  '#16a085', // teal (rápido)
  '#27ae60', // verde oscuro (veloz)
  '#2ecc71'  // verde claro (automatizado)
];

function classifyMath(valor, group, operation) {
  const spec = operation === 'add'
    ? mathAddThresholds[group]
    : mathMultThresholds[group];
  if (!spec) return null;
  for (let key of mathKeys) {
    const { min, max } = spec[key];
    if (valor >= min && valor <= max) return key;
  }
  return null;
}

// —————————————————————————————————
// 5) Auxiliar: extraer valor de student
// —————————————————————————————————
const obtenerValorParaEval = (student, evalKey, tipo) => {
  let dato = null;
  if (['1','2','3','4'].includes(evalKey)) {
    dato = tipo === 'lectora'
      ? student[`reading_eval${evalKey}`]
      : student[`math_eval${evalKey}`];
  } else if (['1a','2a','3a','4a'].includes(evalKey)) {
    dato = student[`math_eval${evalKey}`];
  }
// — Ajuste: ignorar strings vacíos o nulos
if (dato === '' || dato == null) {
  return null;
}
const num = Number(dato);
return isNaN(num) ? null : num;
};

// —————————————————————————————————
// 6) computeSummaryForStudents
// —————————————————————————————————
const computeSummaryForStudents = (list, group, tipo, evalKey) => {
  if (!group) return null;
  if (tipo === 'lectora') {
    const counts = readingKeys.reduce((a,k)=>(a[k]=0,a),{});
    counts.total = 0;
    const key = evalKey === 'todas' ? '1' : evalKey;
    list.forEach(stu => {
      const v = obtenerValorParaEval(stu, key, tipo);
      const cat = v != null && classifyReading(v, group);
      if (cat) { counts[cat]++; counts.total++; }
    });
    return counts;
  } else {
    const counts = mathKeys.reduce((a,k)=>(a[k]=0,a),{});
    counts.total = 0;
    const key = evalKey === 'todas' ? '1' : evalKey;
    const operation = evalKey.endsWith('a') ? 'mult' : 'add';
    list.forEach(stu => {
      const v = obtenerValorParaEval(stu, key, tipo);
      if (v == null) return;
      const cat = classifyMath(v, group, operation);
      if (cat) { counts[cat]++; counts.total++; }
    });
    return counts;
  }
};

// —————————————————————————————————
// 7) prepareChartData
// —————————————————————————————————
const prepareChartData = (summary, tipo) => {
  if (!summary) return { labels:[], datasets:[] };
  if (tipo === 'lectora') {
    const data = readingKeys.map(k => summary[k] || 0);
    return {
      labels: readingLabels,
      datasets: [{ data, backgroundColor: readingColors, hoverOffset: 4 }]
    };
  } else {
    const data = mathKeys.map(k => summary[k] || 0);
    return {
      labels: mathLabels,
      datasets: [{ data, backgroundColor: mathColors, hoverOffset: 4 }]
    };
  }
};

// —————————————————————————————————
// 8) EvolucionChart
// —————————————————————————————————
const EvolucionChart = ({ students, group, tipo, filter }) => {
  const evals = ['1','2','3','4'];
  const countsArr = evals.map(key => {
    const subset = filter === 'promedio'
      ? students
      : students.filter(s => s.nivel === filter);
    return computeSummaryForStudents(subset, group, tipo, key);
  });

  const datasets = [];
  if (tipo === 'lectora') {
    readingKeys.forEach((cat,i) => {
      datasets.push({
        label: readingLabels[i],
        data: countsArr.map(s => s?.[cat] || 0),
        borderColor: readingColors[i],
        backgroundColor: readingColors[i],
        fill: false,
      });
    });
  } else {
    mathKeys.forEach((cat,i) => {
      datasets.push({
        label: mathLabels[i],
        data: countsArr.map(s => s?.[cat] || 0),
        borderColor: mathColors[i],
        backgroundColor: mathColors[i],
        fill: false,
      });
    });
  }

  return (
    <div className="chart-container">
      <Line
        data={{ labels: evals.map(e => `Eval ${e}`), datasets }}
        options={{
          responsive: true,
          plugins: {
            legend: { position: 'top' },
            title: { display: true, text: filter === 'promedio' ? 'Evolución Global' : `Evolución ${filter}` }
          },
          scales: { y: { beginAtZero: true } }
        }}
      />
    </div>
  );
};

// —————————————————————————————————
// 9) Componente principal
// —————————————————————————————————
const ResumenFluidez = () => {
  const [year, setYear] = useState('');
  const [tipo, setTipo] = useState('lectora');
  const [group, setGroup] = useState('');
  const [evaluacion, setEvaluacion] = useState('todas');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // 9.1) Fetch de estudiantes
  useEffect(() => {
    if (!year) return;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `https://asistencia-betterland.onrender.com/fluidez/estudiantes?yearFluidez=${year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        let data = res.ok ? await res.json() : [];
        if (group && groupMapping[group]) {
          data = data.filter(s => groupMapping[group].includes(s.nivel));
        }
        data.sort((a,b) => a.lastnamepaternal.localeCompare(b.lastnamepaternal));
        setStudents(data);
      } catch {
        setStudents([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [year, group]);

  // 9.2) Calcular resumen global
  useEffect(() => {
    if (!group) return;
    setSummary(computeSummaryForStudents(students, group, tipo, evaluacion));
  }, [students, tipo, group, evaluacion]);

  const chartData = prepareChartData(summary, tipo);
  const subniveles = groupMapping[group] || [];

  return (
    <div className="page-content resumen-fluidez">
      <h2>Resumen del Programa de Fluidez</h2>

      {/* — Filtros — */}
      <div className="card filtros-resumen">
        <div className="filtro">
          <label>Año:</label>
          <select value={year} onChange={e => setYear(e.target.value)}>
            <option value="">Seleccione el año</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
          </select>
        </div>
        <div className="filtro">
          <label>Tipo de Fluidez:</label>
          <select value={tipo} onChange={e => setTipo(e.target.value)}>
            <option value="lectora">Fluidez Lectora</option>
            <option value="matematica">Fluidez Matemáticas</option>
          </select>
        </div>
        <div className="filtro">
          <label>Grupo:</label>
          <select value={group} onChange={e => setGroup(e.target.value)}>
            <option value="">Seleccione Grupo</option>
            {groupOrder.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="filtro">
          <label>Evaluación:</label>
          <select value={evaluacion} onChange={e => setEvaluacion(e.target.value)}>
            {tipo === 'lectora' ? (
              <>
                <option value="1">Evaluación 1</option>
                <option value="2">Evaluación 2</option>
                <option value="3">Evaluación 3</option>
                <option value="4">Evaluación 4</option>

              </>
            ) : (
              <>
                <option value="1">Eval 1 (Ad/Sust)</option>
                <option value="2">Eval 2 (Ad/Sust)</option>
                <option value="3">Eval 3 (Ad/Sust)</option>
                <option value="4">Eval 4 (Ad/Sust)</option>
                <option value="1a">Eval 1 (Mult)</option>
                <option value="2a">Eval 2 (Mult)</option>
                <option value="3a">Eval 3 (Mult)</option>
                <option value="4a">Eval 4 (Mult)</option>

              </>
            )}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Cargando datos...</p>
      ) : (
        <>
          <h3>
            {tipo === 'lectora' ? 'Fluidez Lectora' : 'Fluidez Matemáticas'} — 
            Grupo {group} — {year}
          </h3>

          {/* — Resumen Global (tabla) — */}
          {tipo === 'lectora' ? (
            <table className="resumen-table">
              <thead>
                <tr>{readingLabels.map(l => <th key={l}>{l}</th>)}</tr>
              </thead>
              <tbody>
                <tr>{readingKeys.map(k => <td key={k}>{summary?.[k]||0}</td>)}</tr>
              </tbody>
            </table>
          ) : (
            <table className="resumen-table">
              <thead>
                <tr><th>Categoría</th><th>Cantidad</th></tr>
              </thead>
              <tbody>
                {mathKeys.map((k,i) => (
                  <tr key={k}>
                    <td>{mathLabels[i]}</td>
                    <td>{summary?.[k]||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* — Gráficos Globales — */}
          <div className="chart-row">
            <div className="chart-container">
            <Doughnut data={chartData} options={doughnutOptions} />
            </div>
            <EvolucionChart
              students={students}
              group={group}
              tipo={tipo}
              filter="promedio"
            />
          </div>

          {/* — Desglose por Subnivel — */}
          {subniveles.map(sub => {
            const lista = students.filter(s => s.nivel === sub);
            const subSummary = computeSummaryForStudents(lista, group, tipo, evaluacion);
            const subChart   = prepareChartData(subSummary, tipo);

            let gruposNombres;
            if (tipo === 'lectora') {
              gruposNombres = readingKeys.reduce((a,k)=>(a[k]=[],a),{});
              const key = evaluacion === 'todas' ? '1' : evaluacion;
              lista.forEach(s => {
                const v = obtenerValorParaEval(s, key, tipo);
                const c = v != null && classifyReading(v, group);
                if (c) gruposNombres[c].push(s);
              });
            } else {
              gruposNombres = mathKeys.reduce((a,k)=>(a[k]=[],a),{});
              const key = evaluacion === 'todas' ? '1' : evaluacion;
              const operation = evaluacion.endsWith('a') ? 'mult' : 'add';
              lista.forEach(s => {
                const v = obtenerValorParaEval(s, key, tipo);
                if (v == null) return;
                const c = classifyMath(v, group, operation);
                if (c) gruposNombres[c].push(s);
              });
            }

            return (
              <div key={sub} className="subnivel-resumen">
                <h4>Curso {sub}</h4>
                <div className="chart-row">
                  <div className="chart-container">
                  <Doughnut data={subChart} options={doughnutOptions} />
                  </div>
                  <EvolucionChart
                    students={students}
                    group={group}
                    tipo={tipo}
                    filter={sub}
                  />
                </div>
                <div className="categorias-container">
                  {(tipo === 'lectora' ? readingKeys : mathKeys).map((cat,i) => (
                    <div key={cat} className="categoria-column">
                      <h6>
                        {(tipo === 'lectora' ? readingLabels[i] : mathLabels[i])}
                        ({gruposNombres[cat]?.length || 0})
                      </h6>
                      {gruposNombres[cat]?.length > 0 ? (
                        <ul>
                          {gruposNombres[cat].map(s => (
                            <li key={s.rut}>
                              {s.lastnamepaternal} {s.lastnamematernal} {s.firstname}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>No hay estudiantes</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ResumenFluidez;
